/**
 * Event — a festival or standalone happening; the root every ticket type,
 * quota, checkout, order, and ticket hangs off, plus its own public
 * availability rollup.
 *
 * Path: `workspaces/{orgId}/events/{eventId}` — public read; member CRUD
 * behind `tickets.events.manage`, denied until the `mfa: true` claim —
 * EXCEPT `availability`, which only the dedicated availability trigger may
 * write. A generated update rule cannot subtract one field from the
 * document it governs, so the WHOLE member-update rule lives in a hand
 * fragment (`ican.extraMatches`) with its own affected-keys mask; `update`
 * is therefore not declared as a kernel action here, so nothing generated
 * competes with it (the same pattern `WorkspaceSettings` uses for
 * `invoiceCounter`).
 *
 * `delete` is server-only behind `deleteEvent`: the guard fact is whether any
 * order, ticket or non-terminal checkout still names this event, which no rule
 * can query, so the door censuses them inside the deleting transaction and
 * refuses with counts. The kernel rule keeps the manage + mfa predicate — a
 * vacuous one would answer `me.can('delete', Event)` true for any
 * authenticated actor — and `.requiresHandler` is what suppresses its emission.
 */

import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { LocalizedTextSchema, localizedText } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { BookingFeeSchema } from "./booking-fee.entity.ts";
import { LocalizedCopyStatusSchema } from "./localized-copy-status.const.ts";
import { TicketTemplateVariantSchema } from "./ticket-template-variant.const.ts";
import type { Actor } from "./workspace.entity.ts";

// The event's public inventory rollup — one public listener carries schedule,
// location, and availability together. Buckets, never counts: a numeric remainder on a public
// field is a scalper's inventory oracle, and a writer-formatted one ("~200") is
// untranslatable in a DE/EN product. Written by the dedicated trigger watching every
// `quota/inventory` write and every `ticketTypes/{typeId}` write alike (a member's
// capacity or salesWindow edit changes purchasability just as a settle/refund/
// compensation does), debounced (≤1/s, or immediately on a bucket change
// open→low→soldOut).
export const AvailabilitySchema = z
	.record(
		z.string(),
		z.object({
			state: z
				.enum(["open", "low", "soldOut"])
				.describe(
					"the bucket the storefront renders — underivable by this reader, which may never see capacity/consumed; the threshold is applied by the one writer that can",
				),
			// No reader keys on this field. A chained successor's visibility
			// (TicketType.showAfterSoldOut) is answered from the live bucket and the
			// resolved sales window — by advertisableTicketTypes in
			// ticket-type.entity.ts and by the storefront's own event page alike — so
			// a refund reopening a predecessor re-hides its successor. The one-way
			// latch is written by the availability trigger and cleared by purgeOrder,
			// and nothing branches on it.
			//
			// It stands as the document's only record of the instant: `state` carries
			// the live bucket, so once a refund reopens one, when it first sold out is
			// recoverable from nothing else — an observed fact rather than a derived
			// one, which is what separates it from the stored soldOut flag
			// ticket-type.entity.ts excludes as a drift generator.
			soldOutAt: z
				.custom<Timestamp>()
				.nullish()
				.describe(
					"the instant this bucket first reached soldOut — written once, never re-stamped, and never cleared by a later refund or capacity edit reopening the bucket, so it stands on a bucket now reading open. The order purge is the one path that clears it, for every type the purged order touched",
				),
		}),
	)
	.describe(
		"per ticket type — 20k listeners read this field on the event doc, never the quota doc",
	);
export type Availability = z.infer<typeof AvailabilitySchema>;

// Three ceilings sit above this number, in the order they bind.
//
// The orderAccess.tickets[] projection is ONE Firestore document against the
// 1 MiB (1,048,576 B) document ceiling. fulfillTickets
// (firebase/src/events/orders/deliveries/tickets.webhook.ts) writes
// one projection entry per ticket — ticketId, typeName, templateVariant,
// status, entryWindow, qrToken — and the artifacts slots beside it carry one
// Storage path per ticket again. Measured on a guest order, whose ticketId is
// the longer of the two mint shapes: ~455 B per ticket across the whole
// document, ~455 KB at this cap, ~43% of the ceiling, which is reached near
// 1,900 tickets. typeName is the one entry field an operator sizes, and
// TicketTypeSchema.name bounds it at 120 characters — that field's whole
// contribution stays near 120 KB across this cap, so no label an operator can
// write reaches the ceiling on its own.
//
// The ticket-file render POST body (pdf-render's RenderImagesInput)
// against express.json's 10 MiB limit — one ~700 KB shell for the batch, plus
// ~1 KB per frame.
//
// The minting transaction against the 10 MiB request-size and 270-second
// duration limits — one write per ticket plus the orderAccess and marker
// writes, all in one transaction.
export const MAX_TICKETS_PER_ORDER = 1000;

// The poster's fixed upload ratio — event-form.view.ts's own
// <schmancy-image-crop ratio> is the single minting path for every stored
// poster, so this is a fact about the stored bytes, not a styling choice:
// every render site (the crop's own ratio attribute, every poster <img>'s
// intrinsic-size hint, every CSS aspect-ratio reservation) reads it from
// here rather than re-deriving it by hand. WIDTH/HEIGHT is a ratio-faithful
// pixel pair at the crop's own max-dimension="1920" cap (1920 is 2:3's
// taller side; 1920 * 2 / 3 = 1280 is the shorter).
//
// The cap is set by what an advertisement costs. A campaign artwork slot is
// 4:5 or 9:16, and a 2:3 picture yields either only as a crop of itself: the
// 4:5 region is the poster's full width, the 9:16 region its full height at
// 9/16 of it. So a story crop measures 9/16 of the cap, and 1920 is the
// floor that puts it at 1080 — a story slot's own width, no pixel of it
// invented by the encoder. The feed crop takes the full 1280, past its own
// 1080 slot.
//
// The pair is what the minting path produces, not a measurement of every
// object in Storage: a poster stored at a smaller size stays as it is, since
// a stored crop is the frame its operator confirmed and the pixels a larger
// one needs are not in it. Nothing spends the pair as a promise about a file
// — the render sites use it for an intrinsic-size hint and an aspect
// reservation, both of which POSTER_RATIO alone satisfies, and a smaller
// poster scales into the same box.
export const POSTER_RATIO = "2:3";
export const POSTER_WIDTH = 1280;
export const POSTER_HEIGHT = 1920;

// The self-service return policy for one event — the config the buyer's
// refund page and the resolver both read. Every field is the operator's own
// choice; nothing here is derived or trigger-written. `refundableTypeIds` is
// closed by design: a type is refundable only once an operator names it, so
// a tier created mid-sale starts non-refundable, and the event editor is the
// only surface that makes that gap visible.
export const EventRefundConfigSchema = z.object({
	window: z
		.object({ start: z.custom<Timestamp>(), end: z.custom<Timestamp>() })
		.refine((w) => w.end.toMillis() > w.start.toMillis(), {
			message: "the refund window must close after it opens",
		})
		.describe(
			"when this event takes tickets back — a request resolved outside it is not eligible, whatever it carries",
		),
	paidBefore: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"the purchase-time cutoff — an order whose paidAt is at or after this instant is not refundable, one paid strictly before it may be. A full instant, not a day: refunds open because something about the event changed, and the change was announced at an hour, so a buyer who committed minutes after the announcement sits on the far side of this bound. Absent means there is no purchase-time cutoff and the window alone decides who may submit",
		),
	// The shared locale-keyed shape (@momo/i18n's localizedText) over a value
	// that may be explicitly null: the refund card's two boxes are written
	// together, so filling one leaves the other's key present and empty, and
	// documents already carry that pair on disk. Every other locale map in
	// this package clears by dropping the whole field instead.
	message: localizedText(z.string().min(1).nullish())
		.nullish()
		.describe(
			"the operator's own sentence to buyers on the return page, per UI language — absent, or absent for the buyer's own language, renders the surface's default copy",
		),
	refundableTypeIds: z
		.array(z.string())
		.default([])
		.describe(
			"the ticket types this event gives money back for, by type id — a closed whitelist: absent or empty means nothing on this event is refundable, and a type absent from it is non-refundable however it was sold. A deleted type's id is reconciled out of this list, and the resolver treats an id whose type does not resolve as non-refundable rather than trusting the list alone",
		),
});
export type EventRefundConfig = z.infer<typeof EventRefundConfigSchema>;

// events/{eventId} — public read; member CRUD
export const EventSchema = z.object({
	name: z.string().min(1),
	description: z.string().nullish(),
	descriptionByLocale: LocalizedTextSchema.nullish().describe(
		"the event's description in every UI language the storefront serves, written by the localize trigger from `description` — absent, or absent for the reader's own language, renders `description` itself. Read through resolveLocalizedText (@momo/i18n), never keyed directly; the operator's own typed string stays the source of record, and the editor retires this whole map when that string changes, so a pane is never a translation of words the source no longer carries",
	),
	// The description reaches the storefront in a language the operator never sees:
	// the trigger writes the map behind their save, so whether the translation
	// has caught up with the edit they just made, and which pane holds their
	// own words, are answerable nowhere but from the document itself.
	descriptionByLocaleStatus: LocalizedCopyStatusSchema.nullish().describe(
		"where `descriptionByLocale` stands — a pass the operator asked for, a pass in flight, a pass that wrote and which pane holds their own words, or a pass that produced nothing. Absent is an event no pass has run over, which asserts nothing about the description's provenance rather than claiming the machine wrote it",
	),
	poster: z
		.string()
		.nullish()
		.describe(
			"the Storage object path of the event’s uploaded poster image — public read, so the storefront landing grid renders it with no session; a member uploads it behind the same tickets.events.manage + mfa gate Event’s own writes carry. Absent renders the workspace’s branded placeholder, never a broken image.",
		),
	logo: z
		.string()
		.nullish()
		.describe(
			"the Storage object path of the event’s own wordmark image — public read, no forced crop ratio (a wordmark isn’t cropped to a fixed ratio, unlike poster); a member uploads it behind the same tickets.events.manage + mfa gate Event’s own writes carry. Absent renders the generated mirrored wordmark a ticket-template variant draws from the event name itself, never a broken image.",
		),
	schedule: z
		.object({ start: z.custom<Timestamp>(), end: z.custom<Timestamp>() })
		.refine((s) => s.end.toMillis() > s.start.toMillis(), {
			message: "event must end after it starts",
		}),
	locationId: z
		.string()
		.describe(
			"the venue this event takes place at — resolves against the workspace’s location catalog; every event happens somewhere, so this is required, never nullish",
		),
	templateVariant: TicketTemplateVariantSchema.nullish().describe(
		"the code-level rendering variant this event picks as its own default — absent means every ticket type under this event falls through to the package's own built-in default. Resolve via resolveTicketTemplateVariant (ticket-type.entity.ts), never read directly; a ticket type's own templateVariant overrides this per type",
	),
	status: z
		.enum(["draft", "published", "archived"])
		.default("draft")
		.describe(
			"publication state — the landing page lists published events only",
		),
	maxTicketsPerOrder: z
		.number()
		.int()
		.min(1)
		.max(MAX_TICKETS_PER_ORDER)
		.describe(
			"the per-order cap settleOrder enforces authoritatively, re-validated summed over the checkout’s pricedLines; the storefront mirrors it as UX. Ceilinged at MAX_TICKETS_PER_ORDER — an order's whole ticket set lands as one orderAccess document, which Firestore's 1 MiB document ceiling bounds",
		),
	availability: AvailabilitySchema.nullish().describe(
		"the inventory rollup published by the dedicated trigger watching quota/inventory and ticketTypes writes alike — one public field alongside schedule and locationId, absent until the first settle or refusal",
	),
	bookingFee: BookingFeeSchema.nullish().describe(
		"this event's own booking fee, overriding the workspace default whole — resolved through resolveBookingFee (booking-fee.entity.ts), never read directly. Absent is the ordinary state and means the workspace's fee governs, never that this event charges nothing; an event that charges nothing states a fee whose amount is zero. Whole-object like a ticket type's salesWindow: an event states its fee or inherits it, and no fee takes its amount from one document and its return policy from another",
	),
	refund: EventRefundConfigSchema.nullish().describe(
		"the self-service return policy — absent means returns are closed for this event, which the resolver answers as not eligible rather than as an error",
	),
	// Excluded: holdMinutes — nothing is held, so nothing sizes it; the settle-first
	// engine's own checkout-session time budget lives in Revolut's expire_pending_after
	// config, set when the checkout starts, never a stored Firestore field, never rendered as a countdown.
	// Excluded: a rules-visible scanningOpen flag — check-in gating does not yet key off a stored field.
	// Excluded: a stored ticket-selection-mode field — single vs. multi derives at render time from
	// the count of currently-purchasable (published, within salesWindow) kind:'ticket' types; add-ons
	// never count; mid-sale the branch only widens, never removes the selection step.
	// Excluded: tierReleaseMode — release scheduling is now a per-type visibility chain
	// (TicketType.showAfterSoldOut chains a successor to a named predecessor), not a
	// whole-event schedule.
});
export type Event = z.infer<typeof EventSchema>;
// status carries a .default(), as does onPartialReturn inside a bookingFee an
// event states, so a writer that lets EventSchema.parse fill either in types
// its own literal against this input shape rather than the output above.
export type EventInput = z.input<typeof EventSchema>;

// Where this event stands against the clock — the one reading every surface
// asking "is it over?" takes, so the public shelf and the operator console can
// never disagree about which events are finished.
export type Lifecycle = "onSale" | "present" | "after";

// The `end` bound is INCLUSIVE: an event reads "after" only once `now` is
// strictly past `schedule.end`, so a multi-day run already under way reads
// "present" for its whole length instead of turning over on its first evening.
// Pure, and `now` is the caller's — nothing here ticks, so a surface re-reads
// only when something it already watches emits.
export function resolveLifecycle(
	now: Date,
	schedule: { start: Date; end: Date },
): Lifecycle {
	if (now.getTime() < schedule.start.getTime()) return "onSale";
	if (now.getTime() <= schedule.end.getTime()) return "present";
	return "after";
}

export const Event = entity<Actor>()({
	schema: EventSchema,
	actions: ["read", "list", "create", "delete"] as const,
})
	.pipe(({ can, when, hasPermission, and, pathVar }) => [
		// Public: the landing page and storefront browse events with no auth.
		can("read").when(() => and()),
		can("list").when(() => and()),
		// Member create: denied until mfa:true. `update` is intentionally
		// absent from this tuple — see the file-level note above.
		when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.events.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		).allow("create"),
		// Same authority, no emitted allow line — see the file-level note.
		can("delete")
			.when((_data, me) =>
				and(
					hasPermission(me.permissions, "tickets.events.manage", {
						event: pathVar("eventId"),
					}),
					me.mfa.eq(true),
				),
			)
			.requiresHandler("deleteEvent"),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}",
		}),
	);
